import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, Mail, User, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moana-paye-module/artifacts/oaca6hlg_LOGO%20GROUPE%20AERH%20POLYNESIE%20COULEUR%20%28%C3%A0%20utiliser%20sur%20fond%20clair%29%20PNG%20%282%29.png";

const navItems = [
  { name: 'Accueil', path: '/' },
  { name: 'Qui sommes-nous', path: '/a-propos' },
  { 
    name: 'Nos Activités', 
    path: '#',
    children: [
      { name: 'Formations', path: '/formations' },
      { name: 'Consulting', path: '/consulting' },
      { name: 'Audit RSE & Hybride', path: '/audit-rse-hybride' },
      { name: 'Recrutement', path: '/recrutement' },
      { name: 'Offres d\'emploi', path: '/offres' },
      { name: 'Location de salles', path: '/location-salles' },
      { name: 'Moana Paye', path: '/moana-paye' },
    ]
  },
  { name: 'Blog', path: '/blog' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <>
      {/* Top Bar */}
      <div className="hidden md:block bg-teal-700 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+68987737815" className="flex items-center gap-2 hover:text-gold-400 transition-colors">
              <Phone className="w-4 h-4" />
              +689 87 73 78 15
            </a>
            <a href="mailto:contact@groupeaerhpolynesie.com" className="flex items-center gap-2 hover:text-gold-400 transition-colors">
              <Mail className="w-4 h-4" />
              contact@groupeaerhpolynesie.com
            </a>
          </div>
          <span className="text-gold-400 font-medium">10 Rue Georges Lagarde, Papeete</span>
        </div>
      </div>

      {/* Main Navigation */}
      <header 
        className={`sticky top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-md' 
            : 'bg-white'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Groupe AERH Polynésie" 
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <div key={item.name} className="relative">
                  {item.children ? (
                    <div 
                      className="relative"
                      onMouseEnter={() => setDropdownOpen(true)}
                      onMouseLeave={() => setDropdownOpen(false)}
                    >
                      <button 
                        className="flex items-center gap-1 text-slate-700 hover:text-teal-600 font-medium transition-colors py-2"
                        data-testid="activities-dropdown"
                      >
                        {item.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {dropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 mt-1"
                          >
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.path}
                                className="block px-4 py-3 text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                                data-testid={`nav-${child.path.replace('/', '')}`}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-slate-700 hover:text-teal-600 font-medium transition-colors relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-gold-500 after:transition-all hover:after:w-full ${
                        location.pathname === item.path ? 'text-teal-700 after:w-full' : ''
                      }`}
                      data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <Button 
                  asChild
                  className="bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-full px-5 py-5"
                  data-testid="btn-espace-client"
                >
                  <Link to="/espace-client">
                    <User className="w-4 h-4 mr-2" />
                    Mon espace
                  </Link>
                </Button>
              ) : (
                <Button 
                  asChild
                  variant="outline"
                  className="border-teal-600 text-teal-700 hover:bg-teal-50 font-medium rounded-full px-5 py-5"
                  data-testid="btn-connexion"
                >
                  <Link to="/formations?showLogin=true">
                    <LogIn className="w-4 h-4 mr-2" />
                    Connexion
                  </Link>
                </Button>
              )}
              <Button 
                asChild
                className="bg-gold-500 hover:bg-gold-600 text-slate-900 font-medium rounded-full px-6 py-5 shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-contact-btn"
              >
                <Link to="/contact">Nous contacter</Link>
              </Button>
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white p-0">
                <div className="p-6 border-b">
                  <img src={LOGO_URL} alt="AERH" className="h-12" />
                </div>
                <nav className="flex flex-col p-4">
                  {navItems.map((item) => (
                    <div key={item.name}>
                      {item.children ? (
                        <div className="py-2">
                          <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider px-3">
                            {item.name}
                          </span>
                          <div className="mt-2 flex flex-col">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.path}
                                onClick={() => setMobileOpen(false)}
                                className="px-3 py-3 text-slate-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className={`px-3 py-3 text-slate-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg transition-colors ${
                            location.pathname === item.path ? 'bg-teal-50 text-teal-700' : ''
                          }`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}
                  <div className="mt-6 px-3 space-y-3">
                    {user ? (
                      <Button 
                        asChild
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-full"
                      >
                        <Link to="/espace-client" onClick={() => setMobileOpen(false)}>
                          <User className="w-4 h-4 mr-2" /> Mon espace
                        </Link>
                      </Button>
                    ) : (
                      <Button 
                        asChild
                        variant="outline"
                        className="w-full border-teal-600 text-teal-700 font-medium rounded-full"
                      >
                        <Link to="/formations?showLogin=true" onClick={() => setMobileOpen(false)}>
                          <LogIn className="w-4 h-4 mr-2" /> Connexion
                        </Link>
                      </Button>
                    )}
                    <Button 
                      asChild
                      className="w-full bg-gold-500 hover:bg-gold-600 text-slate-900 font-medium rounded-full"
                    >
                      <Link to="/contact" onClick={() => setMobileOpen(false)}>
                        Nous contacter
                      </Link>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
